import {
  Autocomplete,
  Button,
  Grid2 as Grid,
  OutlinedInput,
  Stack,
  SxProps,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { cacheService, setupService } from "../../../../Core/Api/services";
import FullTask from "../../../../Layouts/FullTask";
import FullTaskTopbar from "../../../../Components/Topbars/FullTaskTopbar";
import FormInputText from "../../../../Components/Forms/FormInputText/form-input-text";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import FormAutocomplete from "../../../../Components/Forms/FormAutocomplete/form-autocomplete";
import { produce } from "immer";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { useLocation, useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoIcon from "@mui/icons-material/Info";
import { v4 as uuidv4 } from "uuid";
import { DeleteForm, GetFormIsValid, SetFieldIsValid } from "../../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../../Redux/redux-store";
import { useDispatch } from "react-redux";
import { DbMetadataViewInfos } from "../../../../Core/Api/Types/db-metadata-view-infos";
import { MetadataViewType } from "../../../../Core/Api/Types/metadata-view-type";
import EditMetadataViewGroups from "./edit-metadata-view-groups";
import { DbMetadataViewGroupRow } from "../../../../Core/Api/Types/db-metadata-view-group-row";
import { DbMetadataViewGroupColumn } from "../../../../Core/Api/Types/db-metadata-view-group-column";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridDeleteIcon, GridRowParams } from "@mui/x-data-grid";
import { DbMetadataViewFilter } from "../../../../Core/Api/Types/db-metadata-view-filter";
import ViewListIcon from "@mui/icons-material/ViewList";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { DbMetadataViewGroup } from "../../../../Core/Api/Types/db-metadata-view-group";

function EditOrCreateMetadataViews() {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const enqueueCustomSnackbar = useCustomSnackbar();

  const [newMetadataView, setNewMetadataView] = useState<DbMetadataViewInfos | null>(null);
  const [selectedFilterMetadata, setSelectedFilterMetadata] = useState<string | undefined>(undefined);
  const [metadataFilterValue, setMetadataFilterValue] = useState("");
  const [initializeForms, setInitializeForms] = useState(false);

  const formId = "MetadataViewForm";
  const formIsValid = useAppSelector(GetFormIsValid(formId));

  const isValid = useMemo(() => {
    let valid = true;
    newMetadataView?.groups?.forEach((g) =>
      g.rows?.forEach((r) => {
        if (!r.columns || (r.columns && r.columns.length === 0)) valid = false;
        r.columns?.forEach((c) => {
          if (!c.metadataFieldIds || (c.metadataFieldIds && c.metadataFieldIds.length === 0)) valid = false;
        });
      })
    );
    return valid && formIsValid;
  }, [newMetadataView?.groups, formIsValid]);

  const isEditing = useMemo(() => location.pathname.includes("edit"), [location]);

  useEffect(() => {
    if (!location) return;

    if (!location.state) navigate(routePaths.settingsMetadataViews.path);

    setNewMetadataView({
      ...location.state,
      groups: (location.state as DbMetadataViewInfos).groups?.map((x) => {
        if (!x.rows) return x;
        let flattenMetadataListRow: DbMetadataViewGroupRow = { ...x.rows[0], columns: [] };
        x.rows?.forEach((r) =>
          r.columns?.forEach((c) =>
            c.metadataFieldIds?.forEach((m) => {
              flattenMetadataListRow.columns!.push(
                new DbMetadataViewGroupColumn({
                  metadataFieldIds: [m],
                  metadataFields: [cacheService.GetMetadata().find((y) => y.id === m)!],
                })
              );
            })
          )
        );
        return { ...x, rows: [flattenMetadataListRow], uuid: uuidv4() };
      }),
    });

    setInitializeForms(true);

    return () => {
      dispatch(DeleteForm({ formId: formId }));
    };
  }, [location.pathname]);

  useEffect(() => {
    if (initializeForms && newMetadataView) {
      if (newMetadataView.groups && newMetadataView.groups.length > 0) {
        newMetadataView.groups.forEach((x) => {
          dispatch(
            SetFieldIsValid({
              fieldId: `viewGroupName-${x.uuid}`,
              formId: formId,
              formSectionId: x.uuid!,
              fieldIsValid:
                x.name && x.rows && x.rows[0] && x.rows[0].columns && x.rows[0].columns.length > 0 ? true : false,
              fieldErrorMessage: "",
            })
          );
        });
      }
    }
  }, [initializeForms]);

  const metadataFilterRows = useMemo(() => {
    return newMetadataView?.filters?.map((x) => ({
      ...x,
      name: cacheService.GetMetadata().find((m) => m.id === x.fieldId)?.tagName || "UNKNOWN",
      id: x.fieldId,
    }));
  }, [newMetadataView?.filters]);

  if (newMetadataView === null) return <></>;

  const handleConfirm = () => {
    const parsedView = {
      ...newMetadataView,
      groups: (newMetadataView.groups ?? []).map((g) => ({
        ...g,
        rows: (g.rows ?? []).map((r) => ({
          ...r,
          columns: (r.columns ?? []).map((c) => ({ ...c, metadataFields: null })),
        })),
      })),
    };
    if (isEditing) {
      setupService
        .UpdateMetadataView(parsedView)
        .then((res) => {
          if (res.success) {
            enqueueCustomSnackbar(t("alert:success.metadataViewUpdated"), "success");
            navigate(routePaths.settingsMetadataViews.path);
          } else {
            enqueueCustomSnackbar(res.message ?? t("alert:error.updatingMetadataView"), "error");
          }
        })
        .catch((err) => enqueueCustomSnackbar(t("alert:error.updatingMetadataView"), "error"));
    } else {
      setupService
        .CreateMetadataView(parsedView)
        .then((res) => {
          if (res.success) {
            enqueueCustomSnackbar(t("alert:success.metadataViewUpdated"), "success");
            navigate(routePaths.settingsMetadataViews.path);
          } else {
            enqueueCustomSnackbar(res.message ?? t("alert:error.updatingMetadataView"), "error");
          }
        })
        .catch((err) => enqueueCustomSnackbar(t("alert:error.updatingMetadataView"), "error"));
    }
  };

  const metadataFilterCols: GridColDef[] = [
    {
      field: "fieldId",
      headerName: t("table:headerName.fieldIdUpperCase"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
    },
    { field: "name", headerName: t("table:headerName.metadataName"), flex: CellSize.small, minWidth: CellSize.small },
    { field: "value", headerName: t("common:value"), flex: CellSize.small, minWidth: CellSize.small },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      align: "right",
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title={t("tooltip:title.removeMetadataFilter")}>
                <GridDeleteIcon color="error" />
              </Tooltip>
            }
            label={t("common:remove")}
            onClick={() => {
              setNewMetadataView(
                produce((draft) => {
                  const removeIndex = draft!.filters!.findIndex((x) => x.fieldId === params.id);
                  removeIndex >= 0 && draft!.filters!.splice(removeIndex, 1);
                })
              );
            }}
          />,
        ];
      },
    },
  ];

  const handleAddMetadataFilter = () => {
    let fieldId = cacheService.GetMetadata().find((x) => x.tagName === selectedFilterMetadata)?.id;
    const newFilter = new DbMetadataViewFilter({ fieldId: fieldId, value: metadataFilterValue });
    if (fieldId) {
      setNewMetadataView(
        produce((draft) => {
          draft!.filters ? draft!.filters.push(newFilter) : (draft!.filters = [newFilter]);
        })
      );
      setSelectedFilterMetadata(undefined);
      setMetadataFilterValue("");
    }
  };

  return (
    <FullTask
      sxStack={{
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
        alignItems: "center",
        ...(theme.helpers(theme).scrollableContainer as SxProps),
      }}
      TopBar={
        <FullTaskTopbar
          sx={{ position: "fixed", top: 0, width: "100%", background: theme.palette.background.default }}
          title={
            isEditing
              ? t("page:fullTaskTopbar.title.metadataViewUpdate", { name: newMetadataView.name })
              : t("page:fullTaskTopbar.title.metadataViewCreation")
          }
          backRoute={`${routePaths.settingsMetadataViews.path}`}
          rightContent={
            <Button variant="contained" color="primary" onClick={handleConfirm} disabled={!isValid}>
              {t("common:confirm")}
            </Button>
          }
        />
      }
    >
      <Stack
        sx={{
          ...theme.helpers(theme).scrollableContainer,
          ...theme.helpers(theme).scrollBar,
        }}
        width={"100%"}
        mt={7}
        mb={2}
        pl={2}
        pr={2}
        spacing={2}
      >
        <FormSection
          sx={{ mt: 3 }}
          size="small"
          icon={<InfoIcon />}
          defaultExpanded={true}
          title={t("form:section.title.baseInfosLowerCase")}
        >
          <Grid rowSpacing={4} container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormInputText
                label={t("common:name")}
                fullWidth={true}
                value={newMetadataView!.name}
                placeholder={t("field:placeholder.viewName")}
                onChange={(v) =>
                  setNewMetadataView(
                    produce((draft) => {
                      draft!.name = v.target.value;
                    })
                  )
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionMetaViewBasicInfo",
                  fieldId: "metaView-name",
                  required: true,
                  initialValue: newMetadataView.name ?? undefined,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormAutocomplete
                fullWidth
                options={Object.values(ObjectLayer)}
                label={t("common:objectLayer")}
                onChange={(v) => {
                  setNewMetadataView(
                    produce((draft) => {
                      draft!.objectLayer = v;
                    })
                  );
                }}
                value={newMetadataView.objectLayer ?? undefined}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionMetaViewBasicInfo",
                  fieldId: "metaView-objectLayer",
                  required: true,
                  initialValue: newMetadataView.objectLayer,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormAutocomplete
                fullWidth
                options={Object.values(MetadataViewType)}
                label={t("field:label.viewType")}
                onChange={(v) => {
                  setNewMetadataView(
                    produce((draft) => {
                      draft!.type = v;
                    })
                  );
                }}
                value={newMetadataView.type ?? undefined}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionMetaViewBasicInfo",
                  fieldId: "metaView-type",
                  required: true,
                  initialValue: newMetadataView.type,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormAutocomplete
                fullWidth
                multiple
                options={Object.values(ObjectType)}
                label={t("common:objectTypes")}
                onChange={(v) => {
                  setNewMetadataView(
                    produce((draft) => {
                      draft!.objectTypes = v;
                    })
                  );
                }}
                value={newMetadataView.objectTypes ?? undefined}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionMetaViewBasicInfo",
                  fieldId: "metaView-objectTypes",
                  required: true,
                  minLength: 1,
                  initialValue: newMetadataView.objectTypes ? [...newMetadataView.objectTypes] : [],
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
        <FormSection
          size="small"
          icon={<FilterListIcon />}
          defaultExpanded={true}
          title={t("form:section.title.metadataFilters")}
        >
          <Grid container spacing={2}>
            <Grid
              sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}
              size={{ xs: 12, sm: 12, md: 6 }}
            >
              <Autocomplete
                sx={{ width: "40%", mr: 1, mt: -3 }}
                renderInput={(params) => <TextField {...params} />}
                value={selectedFilterMetadata ?? ""}
                onChange={(e, v) => v && setSelectedFilterMetadata(v)}
                options={cacheService
                  .GetMetadata()
                  .filter((x) => metadataFilterRows?.find((m) => m.fieldId === x.id) === undefined)
                  .map((m) => m.tagName)}
                filterOptions={(x, s) => {
                  let match = cacheService
                    .GetMetadata()
                    .filter((x) => metadataFilterRows?.find((m) => m.fieldId === x.id) === undefined)
                    .map((m) => m.tagName)
                    .filter((x) => x?.toLowerCase().includes(s.inputValue.toLowerCase()));
                  return x.filter((a) => a && match.includes(a));
                }}
              />
              <OutlinedInput
                value={metadataFilterValue}
                onChange={(e) => setMetadataFilterValue(e.target.value)}
                placeholder={t("field:placeholder.enterValueToFilterMetadata")}
                sx={{ width: "40%", mr: 1, height: "40px" }}
              />
              <Button
                sx={{ height: "40px", width: "17%" }}
                disabled={!selectedFilterMetadata || !metadataFilterValue}
                onClick={handleAddMetadataFilter}
                variant="outlined"
                color="primary"
              >
                {t("common:add")}
              </Button>
            </Grid>
            {metadataFilterRows && metadataFilterRows.length > 0 && (
              <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <DataTable
                  noControls
                  disableRowSelectionOnClick
                  rowHeight={32}
                  columnHeaderHeight={32}
                  rows={metadataFilterRows || []}
                  columns={metadataFilterCols}
                />
              </Grid>
            )}
          </Grid>
        </FormSection>
        <FormSection
          defaultExpanded
          size="small"
          title={t("form:section.title.groupsConfiguration")}
          icon={<ViewListIcon />}
        >
          <EditMetadataViewGroups
            newMetadataView={newMetadataView}
            setNewMetadataView={setNewMetadataView}
            formId={formId}
          />
        </FormSection>
      </Stack>
    </FullTask>
  );
}

export default EditOrCreateMetadataViews;
